import React, { useState, useEffect } from "react";
import { useGlobalState } from "../../Context/GlobalContext";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  Tooltip,
  Grid2,
  Menu,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ViewAllFiltersModal from "./ViewAllFiltersModal";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { getBondCharacteristicById } from "../../http/api"; // Add this import
import { processBondData } from "../OrderComponents/utils";
import { clearCharacteristicLinkage } from "../../Utils/viewInventoryHandler";
import FilterChips from "./FilterChips"; // Import the new component
import { useNavigate } from "react-router-dom";
import {
  AutoGraph,
  Grid3x3,
  Grid3x3Outlined,
  Grid3x3Rounded,
  GridOn,
  TableBar,
} from "@mui/icons-material";
import { tokens } from "../../Common/designTokens"; // added token import
import OrderEntry from "../OrderEntry"; // Add this import
import { useActualOrderStore } from "../../Store/actualOrderStore"; // Add this import
import { useAuth } from "../../Context/AuthProvider";
import { utcToLocal } from "../../Utils/timeConversions";
import { stagingOrderFields } from "../../Constants/stagingBlotter";

const InventoryHeader = ({
  handleOEClick,
  input,
  selectedFilter,
  savedFilters,
  totalElements,
  loading,
  lastUpdatedAt,
  csvLoading,
  customizeButtonRef,
  onFilterChange,
  onClearFilters,
  onDeleteChip,
  onManageFiltersOpen,
  onAddFilter,
  onEditSavedFilter, // Add this missing prop
  onCustomizeClick,
  onDownloadCSV,
}) => {
  const [viewAllModalOpen, setViewAllModalOpen] = useState(false);
  const [localSelectedFilter, setLocalSelectedFilter] =
    useState(selectedFilter);
  const [orderEntryOpen, setOrderEntryOpen] = useState(false); // Add state for OrderEntry popup
  const {
    resetBondCharacteristicId,
    characteristicId,
    bondLadderId,
    bondLadderRungId,
    bondCharacteristicData,
    setBondCharacteristicData,
    setCurrentCharacteristicID,
  } = useBondCharacteristicStore();
  const setActualOrderData = useActualOrderStore(state => state.setActualOrderData); // Add this import
  const { token } = useAuth();
  const navigate = useNavigate();
  const { rMode } = useGlobalState();

  // Enable Yield Table button for all users
  const showYieldTable = true;


  useEffect(() => {
    setLocalSelectedFilter(selectedFilter);
  }, [selectedFilter]);

  const handleClearAllFilters = () => {
    onClearFilters();
    setLocalSelectedFilter("");
    clearCharacteristicLinkage();
  };

  const handleDeleteChip = (key) => {
    setLocalSelectedFilter("");
    clearCharacteristicLinkage();
    onDeleteChip(key);
  };

  const displayMode = rMode ? "inline-flex" : "none";

  // NEW: Check if we're in characteristic view (bondCharacteristicId is set)
  const isCharacteristicView = characteristicId && 
    characteristicId !== null && 
    characteristicId !== undefined && 
    characteristicId !== '';
  // NEW: Dummy handler for Add Order from characteristic view
  const handleAddOrderFromCharacteristic = async () => {
    console.log('Add Order from Characteristic View clicked');
    console.log('Characteristic ID:', characteristicId);
    if (!characteristicId) return;

    setCurrentCharacteristicID(characteristicId);

    // Resolve the characteristic first: its account is what the order must be placed on,
    // and the store copy can belong to a different characteristic after a reload. Left null
    // when it cannot be resolved, so a different characteristic's account is never borrowed.
    let characteristic =
      bondCharacteristicData?.characteristicId === characteristicId
        ? bondCharacteristicData
        : null;
    if (!characteristic) {
      try {
        const response = await getBondCharacteristicById(token, characteristicId);
        const flattened = response?.characteristics
          ? processBondData(response)
          : response;
        if (flattened) {
          setBondCharacteristicData(flattened);
          characteristic = flattened;
        }
      } catch (error) {
        console.error("Error fetching bond characteristic details:", error);
      }
    }

    // Ladder-linked characteristics stamp the order with their ladder + rung and route
    // to the trading desk; null / untouched otherwise
    setActualOrderData({
      characteristicId,
      account: characteristic?.account ?? "",
      bondLadderId: bondLadderId ?? null,
      bondLadderRungId: bondLadderRungId ?? null,
      // Explicitly no dealer: this form's Dealer field is editable, so the
      // LEGACY_STAGING_VENUE placeholder stagingOrderFields() falls back to would read as a
      // real choice. Start empty; it fills in from the bond once a CUSIP verifies (and only
      // if that dealer is one the protocol offers). Routing is unaffected either way - the
      // staging flag decides it, and venue is a required field so an empty one cannot be
      // submitted.
      ...(bondLadderId ? stagingOrderFields("") : {}),
    });

    setOrderEntryOpen(true);
  };

  return (
    <Box
      sx={{
        color: tokens.colors.fontColour,
        padding: "16px",
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      {/* Left section - Title and Chips */}
      <Box sx={{ display: "flex", alignItems: "flex-start", flex: 1, gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontSize: { xs: "1rem", sm: "1.25rem" },
              fontWeight: 700,
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
            }}
          >
            Inventory
            {loading ? (
              <CircularProgress sx={{ ml: 0.8, color: tokens.palette.primary }} size={20} />
            ) : (
              ` (${totalElements.toLocaleString()})`
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
              mt: 0.5,
              textAlign: "left",
              whiteSpace: "nowrap",
              color: tokens.colors.fontColour85,
            }}
          >
            Updated at: {utcToLocal(lastUpdatedAt)?.format("DMMMYY HH:mm") ?? "---"}
          </Typography>
        </Box>

        {/* Use the new FilterChips component */}
        <FilterChips
          input={input}
          onDeleteChip={handleDeleteChip}
          onClearFilters={handleClearAllFilters}
          onEditFilter={onAddFilter}
          onEditSavedFilter={onEditSavedFilter} // New prop
          selectedFilter={selectedFilter} // New prop
          savedFilters={savedFilters} // New prop
        />
      </Box>

      {/* Right section - Controls */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}
      >
        <Button
          color="inherit"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOEClick}
          sx={{
            display: displayMode,
            height: "42px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            textTransform: "none",
            boxShadow: "none",
            borderRadius: "8px",
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            width: { xs: "100%", sm: "auto" },
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          Order Entry
        </Button>


        <FormControl sx={{ width: 190, display: isCharacteristicView ? "none" : "block" }}>
          <Select
            value={localSelectedFilter}
            onChange={(e) => {
              setLocalSelectedFilter(e.target.value);
              // Reset characteristic ID when applying saved filters
              resetBondCharacteristicId();
              onFilterChange(e);
            }}
            displayEmpty
            sx={{
              height: 42,
              width: "100%",
              backgroundColor: tokens.colors.defaultGridRowColor,
              color: tokens.colors.fontColour,
              borderRadius: "8px",
              "& .MuiSelect-select": {
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingRight: "8px",
              },
            }}
            renderValue={(selected) => (
              <Tooltip title={selected || "Saved Filters"}>
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: tokens.colors.fontColour,
                  }}
                >
                  {!selected ? "Saved Filters" : selected}
                </Box>
              </Tooltip>
            )}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: 190,
                  maxHeight: 300,
                },
              },
            }}
          >
            {savedFilters.map((filter, index) => (
              <MenuItem
                key={filter.filterName}
                value={filter.filterName}
                sx={{
                  textAlign: "left",
                  borderBottom:
                    index < savedFilters.length - 1
                      ? `1px solid ${tokens.colors.bodyBorderColor50}`
                      : "none",
                  py: 1.5,
                  paddingRight: 4,
                }}
              >
                <Tooltip title={filter.filterName}>
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      width: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {filter.filterName}
                  </Box>
                </Tooltip>
              </MenuItem>
            ))}
            {savedFilters.length > 0 && (
              <Divider
                sx={{ my: 1, borderColor: tokens.colors.bodyBorderColor50 }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                minHeight: 42,
              }}
            >
              <MenuItem
                value="add"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/search-inventory");
                }}
                sx={{
                  p: 0,
                  // pr: 0.5,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                  height: 42,
                  borderRight: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
                }}
              >
                <AddIcon fontSize="small" /> Add
              </MenuItem>
              <MenuItem
                value="manage"
                disabled={savedFilters.length === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  onManageFiltersOpen();
                }}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                  height: 42,
                  px: "10px",
                  opacity: savedFilters.length === 0 ? 0.5 : 1,
                  "&:hover": {
                    backgroundColor:
                      savedFilters.length === 0
                        ? "transparent"
                        : tokens.colors.defaultGridHoverColor,
                  },
                }}
              >
                <FilterListIcon fontSize="small" /> Manage
              </MenuItem>
            </Box>
          </Select>
        </FormControl>
        
        {/* NEW: Add Order button - only show in characteristic view */}
        {isCharacteristicView && (
          <Button
            variant="contained"
            color="inherit"
            startIcon={<AddIcon />}
            sx={{
              height: 42,
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: tokens.colors.defaultGridRowColor,
              color: tokens.colors.fontColour,
              border: `1px solid ${tokens.colors.bodyBorderColor50}`,
              "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
            }}
            onClick={handleAddOrderFromCharacteristic}
          >
            Add Order
          </Button>
        )}
        {/* Yield Table Button - only render for allowed userIds */}
        {showYieldTable && !isCharacteristicView && (
          <Button
            variant="contained"
            color="inherit"
            startIcon={<GridOn />}
            sx={{
              height: 42,
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: tokens.colors.defaultGridRowColor,
              color: tokens.colors.fontColour,
              border: `1px solid ${tokens.colors.bodyBorderColor50}`,
              "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate("/inventory/yield-table");
            }}
          >
            Yield Table
          </Button>
        )}
        <Button
          variant="contained"
          color="inherit"
          startIcon={<FilterAltOutlinedIcon />}
          sx={{
            height: 42,
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
          //on click call on add filter with input as parameter
          onClick={(e) => {
            e.stopPropagation();
            onAddFilter(input);
          }}
        >
          Search
        </Button>

        <Button
          ref={customizeButtonRef}
          variant="contained"
          color="inherit"
          startIcon={<ViewColumnOutlinedIcon />}
          sx={{
            height: 42,
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
          onClick={onCustomizeClick}
        >
          Columns
        </Button>

        <DownloadMenu csvLoading={csvLoading} onSelectFormat={onDownloadCSV} />
      </Box>

      {/* Add OrderEntry popup */}
      {orderEntryOpen && (
        <OrderEntry
          open={orderEntryOpen}
          onClose={() => setOrderEntryOpen(false)}
          source="orderViaCharacteristic"
          initialActiveTab={0}
          initialData={{ characteristicId }}
        />
      )}
    </Box>
  );
};

const DownloadMenu = ({ csvLoading, onSelectFormat }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);
  return (
    <>
      <Tooltip title="Download">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={csvLoading}
          sx={{
            borderRadius: "8px",
            color: tokens.colors.fontColour,
            width: 42,
            height: 42,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          {csvLoading ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={close}>
        <MenuItem onClick={() => { close(); onSelectFormat("csv"); }}>Download CSV</MenuItem>
        <MenuItem onClick={() => { close(); onSelectFormat("xlsx"); }}>Download Excel</MenuItem>
      </Menu>
    </>
  );
};

export default InventoryHeader;
